package one.healthium.life.presentation.feature.today.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import loco.GENERAL_CARBO
import loco.GENERAL_FAT
import loco.GENERAL_FIBRE
import loco.GENERAL_PROTEIN
import loco.LIFE_TODAY_CALORIES_COUNTER_CALORIES_GOAL_FORMAT
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.base.utils.orZero
import one.healthium.life.presentation.navigation.main.PoppinsFontFamily
import one.healthium.loco.resolve
import one.healthium.today.ui.calories_counter.CaloriesCounterUiModel
import one.healthium.today.ui.calories_counter.NutritionGoalUiModel
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.Themes
import one.healthium.ui.compose.theme.green_11
import one.healthium.ui.compose.theme.green_11_15
import one.healthium.ui.compose.theme.green_11_50
import one.healthium.ui.compose.theme.green_13
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview

@Composable
internal fun TodayCaloriesCounter(
    modifier: Modifier = Modifier,
    onLogFoodClicked: () -> Unit,
    model: CaloriesCounterUiModel?,
) {
    Box(modifier) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 28.dp)
                .background(
                    color = green_13,
                    shape = AppTheme.shapes.medium,
                ),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(Modifier.height(36.dp))

            CaloriesProgress(model?.caloriesGoal)

            Spacer(Modifier.height(42.dp))
            Row {
                Column {
                    CarboProgress(model?.carbohydratesGoal)
                    Spacer(Modifier.height(16.dp))
                    FatProgress(model?.fatGoal)
                }
                Spacer(Modifier.width(38.dp))
                Column {
                    ProteinProgress(model?.proteinGoal)
                    Spacer(Modifier.height(16.dp))
                    FibreProgress(model?.fiberGoal)
                }
            }
            Spacer(Modifier.height(48.dp))
        }
        AddButton(onClick = onLogFoodClicked)
    }

}

@Composable
fun CaloriesProgress(goal: NutritionGoalUiModel?) {
    val animatedProgress by animateFloatAsState(
        targetValue = goal?.progress ?: 0f,
        animationSpec = tween(durationMillis = 400, delayMillis = 200),
    )

    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.BottomCenter,
    ) {
        ArcProgressIndicator(
            modifier = Modifier.width(216.dp).height(110.dp),
            color = AppTheme.colors.secondary,
            trackColor = AppTheme.colors.secondary.copy(alpha = 0.3f),
            strokeWidth = 24.dp,
            progress = { animatedProgress },
        )
        Column(
            modifier = Modifier.offset(y = 20.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = "🔥",
                style = AppTheme.textStyles.secondary_24_600,
            )
            Spacer(Modifier.height(4.dp))
            Text(
                modifier = Modifier.testTag("calories_progress"),
                text = goal?.amount.formatCalories(addUnit = false).resolve(),
                style = AppTheme.textStyles.secondary_24_600,
                color = green_11,
            )
            Spacer(Modifier.height(4.dp))
            Text(
                text = stringResource(
                    loco.Res.string.LIFE_TODAY_CALORIES_COUNTER_CALORIES_GOAL_FORMAT,
                    goal?.goal.formatCalories().resolve()
                ),
                style = AppTheme.textStyles.secondary_12_400,
                color = green_11_50,
            )
        }
    }
}


@Composable
private fun CarboProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_CARBO),
    )
}

@Composable
private fun ProteinProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_PROTEIN),
    )
}

@Composable
private fun FatProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_FAT)
    )
}

@Composable
private fun FibreProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(
        goal = goal,
        name = stringResource(loco.Res.string.GENERAL_FIBRE)
    )
}

@Composable
private fun NutritionalGoalItem(
    goal: NutritionGoalUiModel?,
    name: String,
) {
    Column {
        NutritionGoalItemValue(goal)
        Spacer(Modifier.height(8.dp))
        LinearProgressIndicator(
            modifier = Modifier.width(80.dp).height(3.dp),
            color = green_11,
            trackColor = green_11_15,
            strokeCap = StrokeCap.Round,
            progress = { goal?.progress ?: 0f },
            gapSize = 0.dp,
            drawStopIndicator = {},
        )
        Spacer(Modifier.height(4.dp))
        Text(
            text = name,
            style = AppTheme.textStyles.secondary_12_400,
            color = green_11_50,
        )
    }
}

@Composable
private fun NutritionGoalItemValue(goal: NutritionGoalUiModel?) {
    Text(
        text = goal?.amount.orZero().formatGrams().resolve(),
        style = AppTheme.textStyles.secondary_16_400,
        color = green_11,
    )
}

@Composable
private fun ArcProgressIndicator(
    modifier: Modifier = Modifier,
    color: Color,
    trackColor: Color,
    strokeWidth: Dp = 24.dp,
    progress: () -> Float,
) {
    val coercedProgress = { progress().coerceIn(0f, 1f) }

    Canvas(modifier = modifier) {
        val currentCoercedProgress = coercedProgress()

        val arcSize = Size(size.width, size.height * 2)

        drawArc(
            color = trackColor,
            startAngle = 0f,
            sweepAngle = -180f,
            useCenter = false,
            size = arcSize,
            style = Stroke(
                width = strokeWidth.toPx(),
                cap = StrokeCap.Round
            )
        )

        drawArc(
            color = color,
            startAngle = 180f,
            sweepAngle = 180f * currentCoercedProgress,
            useCenter = false,
            size = arcSize,
            style = Stroke(
                width = strokeWidth.toPx(),
                cap = StrokeCap.Round
            )
        )
    }
}

@Composable
private fun BoxScope.AddButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(56.dp)
            .clip(CircleShape)
            .background(AppTheme.colors.primary, CircleShape)
            .align(Alignment.BottomCenter)
            .clickable { onClick() },
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = "+",
            style = AppTheme.textStyles.poppins32SemiBold,
            color = AppTheme.colors.textInverted100,
        )
    }
}

@Composable
@Preview
private fun TodayCaloriesCounterPreview() {
    AppTheme(Themes.life(PoppinsFontFamily)) {
        TodayCaloriesCounter(
            model = CaloriesCounterUiModel(
                caloriesGoal = NutritionGoalUiModel(2000f, 500f),
                carbohydratesGoal = NutritionGoalUiModel(100f, 50f),
                proteinGoal = NutritionGoalUiModel(50f, 30f),
                fatGoal = NutritionGoalUiModel(70f, 40f),
                fiberGoal = NutritionGoalUiModel(30f, 20f),
            ),
            onLogFoodClicked = {},
        )
    }
}
